// Three-column menu drawer: column 1 selects a top-level item (hover, focus, or click),
// column 2 shows that item's panel, column 3 previews the item or the hovered sub-link image.
class SMenuColumns extends HTMLElement {
  #abort;
  #current = '';

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.items = Array.from(this.querySelectorAll('[data-menu-item]'));
    this.panels = Array.from(this.querySelectorAll('[data-menu-panel]'));
    this.media = Array.from(this.querySelectorAll('[data-menu-media]'));

    for (const item of this.items) {
      const key = item.dataset.menuItem;
      item.addEventListener('pointerenter', () => this.select(key), { signal });
      item.addEventListener('focus', () => this.select(key), { signal });
      if (item.tagName === 'BUTTON') {
        item.addEventListener(
          'click',
          () => {
            this.select(key);
            this.querySelector(`[data-menu-panel="${key}"] a`)?.focus();
          },
          { signal }
        );
      }
    }

    for (const link of this.querySelectorAll('[data-menu-preview]')) {
      const show = () => this.preview(link.dataset.menuPreview);
      link.addEventListener('pointerenter', show, { signal });
      link.addEventListener('focus', show, { signal });
    }
    for (const panel of this.panels) {
      panel.addEventListener('pointerleave', () => this.preview(this.#current), { signal });
    }

    // Header shortcuts (e.g. ÜRÜNLER) open this drawer on a chosen item; without JS they stay plain links.
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest?.('[data-menu-select]');
        if (!trigger) return;
        const dialog = document.getElementById(trigger.dataset.menuOpen || '');
        if (!(dialog instanceof HTMLDialogElement) || !dialog.contains(this)) return;
        event.preventDefault();
        this.select(trigger.dataset.menuSelect);
        if (!dialog.open) dialog.showModal();
      },
      { signal }
    );

    const initial = this.items.find((item) => item.tagName === 'BUTTON') || this.items[0];
    if (initial) this.select(initial.dataset.menuItem);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  select(key) {
    if (!key) return;
    this.#current = key;
    for (const item of this.items) {
      const active = item.dataset.menuItem === key;
      item.toggleAttribute('data-active', active);
      if (item.tagName === 'BUTTON') item.setAttribute('aria-expanded', String(active));
    }
    for (const panel of this.panels) panel.hidden = panel.dataset.menuPanel !== key;
    this.preview(key);
  }

  preview(key) {
    let found = false;
    for (const media of this.media) {
      const match = media.dataset.menuMedia === key;
      media.hidden = !match;
      found ||= match;
    }
    if (!found) {
      const fallback = this.media.find((media) => media.dataset.menuMedia === this.#current) || this.media.find((m) => m.hasAttribute('data-menu-media-default'));
      if (fallback) fallback.hidden = false;
    }
  }
}

if (!customElements.get('s-menu-columns')) customElements.define('s-menu-columns', SMenuColumns);
